// @ts-nocheck
import React, { useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@libraryadmin/components/ui/dialog";
import { Button } from "@libraryadmin/components/ui/button";
import { Badge } from "@libraryadmin/components/ui/badge";
import {
  Printer,
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  BookOpen,
  ShieldCheck,
  RotateCw,
  CreditCard,
  Building2,
  Tag,
  Clock,
} from "lucide-react";
import type { Member } from "@libraryadmin/data/seed";

interface MemberIDCardModalProps {
  member: Member | null;
  isOpen: boolean;
  onClose: () => void;
  isAutoGenerated?: boolean;
}

export const MemberIDCardModal: React.FC<MemberIDCardModalProps> = ({
  member,
  isOpen,
  onClose,
  isAutoGenerated = false,
}) => {
  const [activeSide, setActiveSide] = useState<"front" | "back" | "both">(
    "front"
  );
  const frontCardRef = useRef<HTMLDivElement>(null);
  const backCardRef = useRef<HTMLDivElement>(null);

  if (!member) {
    return null;
  }

  // Calculate default membership expiry date (1 year after joined date)
  const getExpiryDate = (joinedDateStr: string, customExpiry?: string) => {
    if (customExpiry) {
      return customExpiry;
    }
    if (!joinedDateStr) {
      return "2025-12-31";
    }
    try {
      const parts = joinedDateStr.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10) + 1;
        return `${year}-${parts[1]}-${parts[2]}`;
      }
      const d = new Date(joinedDateStr);
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split("T")[0];
    } catch {
      return "2025-12-31";
    }
  };

  const expiryDate = getExpiryDate(member.joinedDate, member.expiryDate);

  // URL payload so scanning with a phone opens the member's specific page
  const qrPayload = `${window.location.origin}/library-admin/members/${member.id}`;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.print();
      return;
    }

    const frontHTML = frontCardRef.current?.outerHTML || "";
    const backHTML = backCardRef.current?.outerHTML || "";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Horizontal Member Card - ${member.name} (${member.memberId})</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 15mm;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #ffffff;
              color: #0f172a;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              padding: 20px;
            }
            .print-title {
              margin-bottom: 20px;
              text-align: center;
            }
            .print-title h1 {
              font-size: 18px;
              color: #0369a1;
            }
            .print-title p {
              font-size: 12px;
              color: #64748b;
            }
            .cards-container {
              display: flex;
              flex-direction: row;
              gap: 24px;
              align-items: center;
              justify-content: center;
              flex-wrap: wrap;
            }

            /* Identical 1:1 dimension spec for Front & Back cards */
            .card-wrapper {
              width: 460px;
              height: 270px;
              min-width: 460px;
              min-height: 270px;
              border-radius: 16px;
              overflow: hidden;
              border: 2px solid #0284c7;
              box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
              background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              position: relative;
              box-sizing: border-box;
            }

            .card-header {
              height: 52px;
              background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
              color: #ffffff;
              padding: 0 16px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .card-header h2 {
              margin: 0;
              font-size: 14px;
              font-weight: 700;
              letter-spacing: 0.5px;
              text-transform: uppercase;
            }
            .card-header p {
              margin: 2px 0 0 0;
              font-size: 9px;
              opacity: 0.9;
            }
            .badge-side {
              background: rgba(255, 255, 255, 0.2);
              border: 1px solid rgba(255, 255, 255, 0.4);
              color: #ffffff;
              font-size: 9px;
              font-weight: 700;
              padding: 2px 8px;
              border-radius: 9999px;
              text-transform: uppercase;
            }
            
            /* Front Body Styling */
            .card-body-front {
              padding: 14px 16px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
              flex: 1;
            }
            .member-profile-section {
              display: flex;
              align-items: center;
              gap: 14px;
              flex: 1;
            }
            .avatar-frame {
              width: 72px;
              height: 72px;
              border-radius: 50%;
              background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
              color: #0284c7;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 28px;
              font-weight: 800;
              border: 3px solid #38bdf8;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              overflow: hidden;
            }
            .avatar-frame img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .member-details-front {
              display: flex;
              flex-direction: column;
              gap: 3px;
            }
            .member-name {
              font-size: 16px;
              font-weight: 800;
              color: #0f172a;
              line-height: 1.2;
            }
            .member-id-pill {
              display: inline-flex;
              align-items: center;
              background: #0284c7;
              color: #ffffff;
              font-family: monospace;
              font-weight: 700;
              font-size: 12px;
              padding: 2px 8px;
              border-radius: 6px;
              width: fit-content;
              letter-spacing: 0.5px;
            }
            .dates-strip-front {
              display: flex;
              gap: 12px;
              margin-top: 4px;
              background: rgba(2, 132, 199, 0.06);
              padding: 4px 8px;
              border-radius: 6px;
              border: 1px solid rgba(2, 132, 199, 0.15);
            }
            .date-box {
              display: flex;
              flex-direction: column;
            }
            .date-lbl {
              font-size: 8px;
              color: #64748b;
              font-weight: 600;
              text-transform: uppercase;
            }
            .date-val {
              font-size: 10px;
              color: #0f172a;
              font-weight: 700;
              font-family: monospace;
            }

            .qr-section {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: #ffffff;
              padding: 8px;
              border-radius: 12px;
              border: 1px solid #cbd5e1;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            .qr-label {
              font-size: 8px;
              color: #64748b;
              margin-top: 4px;
              font-weight: 600;
              text-align: center;
            }

            /* Back Body Styling */
            .card-body-back {
              padding: 14px 16px;
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px 16px;
              font-size: 11px;
            }
            .info-item {
              display: flex;
              flex-direction: column;
            }
            .info-label {
              font-size: 9px;
              color: #64748b;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.4px;
            }
            .info-val {
              font-size: 11px;
              color: #0f172a;
              font-weight: 600;
            }
            .full-address {
              grid-column: span 2;
            }
            .terms-notice {
              font-size: 8px;
              color: #64748b;
              background: #f1f5f9;
              padding: 6px 10px;
              border-radius: 6px;
              border-left: 3px solid #0284c7;
              line-height: 1.3;
            }

            .card-footer {
              height: 28px;
              background: #f8fafc;
              padding: 0 16px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 9px;
              color: #64748b;
              border-top: 1px solid #e2e8f0;
            }
            @media print {
              body {
                padding: 0;
                min-height: auto;
              }
              .card-wrapper {
                box-shadow: none;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-title">
            <h1>Jambushrushti Jain Library — Official Member ID Card</h1>
            <p>Member Name: <strong>${member.name}</strong> | ID: <strong>${member.memberId}</strong> | Date Generated: ${new Date().toLocaleDateString()}</p>
          </div>
          <div class="cards-container">
            ${frontHTML}
            ${backHTML}
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl overflow-hidden border-border bg-card p-0 shadow-xl">
        {/* Banner notification if auto-generated upon creation */}
        {isAutoGenerated && (
          <div className="flex items-center justify-between border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-2.5 text-emerald-600 dark:text-emerald-400">
            <div className="flex items-center gap-2 text-xs font-semibold">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Member Registered Successfully! Horizontal Member Card Ready.
            </div>
            <Badge
              variant="outline"
              className="border-emerald-500/30 bg-emerald-500/20 text-[10px] text-emerald-600"
            >
              Auto-Generated
            </Badge>
          </div>
        )}

        <DialogHeader className="flex flex-col gap-2 border-b p-4 pb-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <DialogTitle className="flex items-center gap-2 text-base text-primary">
              <CreditCard className="h-5 w-5 text-primary" /> Horizontal Member
              ID Card
            </DialogTitle>
            <DialogDescription className="text-xs">
              Official Library ID Card. Front contains photo, name, card ID,
              joined date, expiry date & QR code. Back contains full contact
              details.
            </DialogDescription>
          </div>

          {/* Toggle buttons for Front / Back / Both */}
          <div className="flex items-center gap-1 self-start rounded-lg bg-muted p-1 sm:self-auto">
            <Button
              variant={activeSide === "front" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveSide("front")}
              className="h-7 px-3 text-xs"
            >
              Front Side
            </Button>
            <Button
              variant={activeSide === "back" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveSide("back")}
              className="h-7 px-3 text-xs"
            >
              Back Side
            </Button>
            <Button
              variant={activeSide === "both" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveSide("both")}
              className="h-7 px-3 text-xs"
            >
              Both Sides
            </Button>
          </div>
        </DialogHeader>

        {/* Preview Area */}
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-6 bg-muted/20 p-6">
          {/* FRONT SIDE CARD */}
          {(activeSide === "front" || activeSide === "both") && (
            <div className="flex flex-col items-center">
              {activeSide === "both" && (
                <span className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <CreditCard className="h-3.5 w-3.5 text-primary" /> Front Side
                </span>
              )}

              {/* Horizontal Card Container (Front) - Fixed 460px x 270px */}
              <div
                ref={frontCardRef}
                className="card-wrapper relative flex h-[270px] min-h-[270px] w-[460px] min-w-[460px] max-w-full shrink-0 flex-col justify-between overflow-hidden rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-background via-sky-50/50 to-primary/10 shadow-lg transition-all dark:via-sky-950/20 dark:to-primary/20"
              >
                {/* Top Banner Header (Fixed 52px height) */}
                <div className="card-header flex h-[52px] shrink-0 items-center justify-between bg-gradient-to-r from-primary via-sky-700 to-sky-800 p-3 px-4 text-primary-foreground shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 font-bold">
                      <BookOpen className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xs font-extrabold uppercase leading-tight tracking-wide sm:text-sm">
                        Jambushrushti Jain Library
                      </h2>
                      <p className="text-[9px] font-medium text-primary-foreground/90">
                        Identification Card
                      </p>
                    </div>
                  </div>
                  <span className="badge-side rounded-full border border-white/30 bg-white/20 px-2 py-0.5 text-[9px] font-bold uppercase text-white">
                    FRONT
                  </span>
                </div>

                {/* Card Body Front */}
                <div className="card-body-front flex flex-1 items-center justify-between gap-3 p-3.5 px-4">
                  {/* Left Side: Avatar Profile Image + Name + Card ID + Joined/Expiry Dates (NO STATUS) */}
                  <div className="member-profile-section flex min-w-0 flex-1 items-center gap-3">
                    {/* Member Profile Image Frame */}
                    <div className="avatar-frame flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary bg-gradient-to-br from-primary/20 to-sky-200 text-2xl font-extrabold text-primary shadow-md dark:to-sky-900 sm:h-20 sm:w-20 sm:text-3xl">
                      {member.avatarUrl ? (
                        <img
                          src={member.avatarUrl}
                          alt={member.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        member.name.charAt(0).toUpperCase()
                      )}
                    </div>

                    {/* Member Details */}
                    <div className="member-details-front flex min-w-0 flex-col gap-1">
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Member Name
                      </span>
                      <h3 className="member-name truncate text-sm font-bold leading-tight text-foreground sm:text-base">
                        {member.name}
                      </h3>

                      <div className="mt-0.5 flex items-center gap-2">
                        <span className="member-id-pill rounded bg-primary px-2 py-0.5 font-mono text-xs font-bold text-primary-foreground shadow-sm">
                          {member.memberId}
                        </span>
                      </div>

                      {/* Joined Date and Expiry Date on Front */}
                      <div className="dates-strip-front mt-1 flex items-center gap-3 rounded-md border border-primary/20 bg-primary/5 p-1.5 px-2 text-[10px] dark:bg-primary/20">
                        <div className="date-box flex flex-col">
                          <span className="date-lbl flex items-center gap-0.5 text-[8px] font-semibold uppercase text-muted-foreground">
                            <Calendar className="h-2.5 w-2.5 text-primary" />{" "}
                            Joined
                          </span>
                          <span className="date-val font-mono font-bold text-foreground">
                            {member.joinedDate}
                          </span>
                        </div>
                        <div className="h-5 w-px bg-border/60" />
                        <div className="date-box flex flex-col">
                          <span className="date-lbl flex items-center gap-0.5 text-[8px] font-semibold uppercase text-muted-foreground">
                            <Clock className="h-2.5 w-2.5 text-primary" />{" "}
                            Expiry
                          </span>
                          <span className="date-val font-mono font-bold text-primary">
                            {expiryDate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Side: Digital QR Code */}
                  <div className="qr-section flex shrink-0 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
                    <div className="p-0.5">
                      <QRCodeSVG
                        value={qrPayload}
                        size={85}
                        level="H"
                        includeMargin={false}
                        aria-label={`QR Code for ${member.memberId}`}
                      />
                    </div>
                    <span className="qr-label mt-1 text-[8px] font-semibold text-slate-500">
                      SCAN VERIFY
                    </span>
                  </div>
                </div>

                {/* Card Footer (Fixed 28px height) */}
                <div className="card-footer flex h-[28px] shrink-0 items-center justify-between border-t border-border/50 bg-muted/60 px-4 text-[9px] text-muted-foreground">
                  <span className="flex items-center gap-1 font-medium">
                    <ShieldCheck className="h-3 w-3 text-primary" /> Library
                    Access Pass
                  </span>
                  <span className="font-mono">VALID FOR ISSUE / RETURN</span>
                </div>
              </div>
            </div>
          )}

          {/* BACK SIDE CARD */}
          {(activeSide === "back" || activeSide === "both") && (
            <div className="flex flex-col items-center">
              {activeSide === "both" && (
                <span className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <CreditCard className="h-3.5 w-3.5 text-primary" /> Back Side
                </span>
              )}

              {/* Horizontal Card Container (Back) - Fixed 460px x 270px (EXACT SAME DIMENSION AS FRONT) */}
              <div
                ref={backCardRef}
                className="card-wrapper relative flex h-[270px] min-h-[270px] w-[460px] min-w-[460px] max-w-full shrink-0 flex-col justify-between overflow-hidden rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-background via-sky-50/40 to-slate-100 shadow-lg transition-all dark:via-sky-950/20 dark:to-slate-900"
              >
                {/* Top Banner Header (Fixed 52px height) */}
                <div className="card-header flex h-[52px] shrink-0 items-center justify-between bg-gradient-to-r from-slate-800 via-slate-900 to-sky-900 p-3 px-4 text-white shadow-sm">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-sky-400" />
                    <div>
                      <h2 className="text-xs font-bold uppercase tracking-wide">
                        MEMBER CONTACT & DETAILS
                      </h2>
                      <p className="text-[9px] font-medium text-slate-300">
                        Jambushrushti Jain Library
                      </p>
                    </div>
                  </div>
                  <span className="badge-side rounded-full border border-white/30 bg-white/20 px-2 py-0.5 text-[9px] font-bold uppercase text-white">
                    BACK
                  </span>
                </div>

                {/* Card Body Back */}
                <div className="card-body-back flex flex-1 flex-col justify-between p-3.5 px-4 text-xs">
                  {/* Detailed Grid Info */}
                  <div className="info-grid grid grid-cols-2 gap-2">
                    <div className="info-item flex flex-col">
                      <span className="info-label flex items-center gap-1 text-[9px] font-semibold text-muted-foreground">
                        <Phone className="h-3 w-3 text-primary" /> Phone Number
                      </span>
                      <span className="info-val font-semibold text-foreground">
                        {member.phone}
                      </span>
                    </div>

                    <div className="info-item flex flex-col">
                      <span className="info-label flex items-center gap-1 text-[9px] font-semibold text-muted-foreground">
                        <Mail className="h-3 w-3 text-primary" /> Email Address
                      </span>
                      <span className="info-val max-w-[170px] truncate font-semibold text-foreground">
                        {member.email || "N/A"}
                      </span>
                    </div>

                    <div className="info-item flex flex-col">
                      <span className="info-label flex items-center gap-1 text-[9px] font-semibold text-muted-foreground">
                        <Tag className="h-3 w-3 text-primary" /> Status / Ref
                      </span>
                      <span className="info-val font-semibold capitalize text-foreground">
                        {member.status}{" "}
                        {member.referenceId
                          ? `(Ref: ${member.referenceId})`
                          : ""}
                      </span>
                    </div>

                    <div className="info-item flex flex-col">
                      <span className="info-label flex items-center gap-1 text-[9px] font-semibold text-muted-foreground">
                        <Clock className="h-3 w-3 text-primary" /> Expiry Status
                      </span>
                      <span className="info-val font-mono font-semibold text-foreground">
                        Valid till {expiryDate}
                      </span>
                    </div>

                    <div className="full-address col-span-2 grid-cols-2 rounded-lg border border-border/40 bg-muted/40 p-2">
                      <span className="info-label flex items-center gap-1 text-[9px] font-semibold text-muted-foreground">
                        <MapPin className="h-3 w-3 text-primary" /> Registered
                        Address
                      </span>
                      <span className="info-val mt-0.5 block text-[11px] font-medium text-foreground">
                        {member.address}, {member.city}, {member.state} -{" "}
                        {member.pincode}
                      </span>
                    </div>
                  </div>

                  {/* Terms & Instructions */}
                  <div className="terms-notice line-clamp-2 rounded-md border-l-2 border-primary bg-muted/50 p-1.5 px-2 text-[8.5px] leading-tight text-muted-foreground">
                    This card is non-transferable and remains property of
                    Jambushrushti Jain Library. Please report lost cards
                    immediately. Must be presented for book borrowing & library
                    entry.
                  </div>
                </div>

                {/* Card Footer (Fixed 28px height) */}
                <div className="card-footer flex h-[28px] shrink-0 items-center justify-between border-t border-border/50 bg-muted/60 px-4 text-[9px] text-muted-foreground">
                  <span>Member Identification Card</span>
                  <span className="font-semibold text-foreground">
                    Authorized Signature & Seal
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Action Buttons */}
        <DialogFooter className="flex flex-row items-center justify-between gap-2 border-t border-border bg-muted/30 p-4">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>

          <div className="flex items-center gap-2">
            <Button
              onClick={() =>
                setActiveSide(activeSide === "front" ? "back" : "front")
              }
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 text-xs"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Flip to {activeSide === "front" ? "Back" : "Front"}
            </Button>

            <Button
              onClick={handlePrint}
              className="btn-primary flex items-center gap-1.5 text-xs"
              size="sm"
            >
              <Printer className="h-4 w-4" />
              Print Horizontal Card (Both Sides)
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
